import React, { useCallback } from 'react';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useTimelineCreation = (
    state: PlannerState, refs: PlannerRefs, openCreateTaskModal: (startDate?: Date, delay?: number) => void
) => {
    const {
        addTaskDate, addTaskDuration, addTaskTime, dragStartHour, dragStartOffset, dragStartY,
        hasDragged, isDragging, setAddTaskDate, setAddTaskDuration, setAddTaskTime, setCurrentMouseY,
        setDraftSelection, setDragStartHour, setDragStartOffset, setDragStartY, setHasDragged,
        setIsDragging, setPendingTouchCreate, setTimeSlotHeight,
    } = state;
    const { pendingCreateListenersRef, pendingTouchCreateRef } = refs;
    const createDraftSelection = useCallback((duration = addTaskDuration) => {
        if (addTaskDate && addTaskTime) {
            setDraftSelection({
                date: new Date(addTaskDate), startTime: addTaskTime,
                startOffset: dragStartOffset, duration: Math.max(30, duration),
            });
        }
    }, [addTaskDate, addTaskDuration, addTaskTime, dragStartOffset, setDraftSelection]);
    const confirmDraftSelection = useCallback(() => {
        const draft = state.draftSelection;
        if (!draft) return;
        const startDate = new Date(draft.date);
        const [hours, minutes] = draft.startTime.split(':').map(Number);
        startDate.setHours(hours, minutes, 0, 0);
        setAddTaskDate(startDate);
        setAddTaskTime(draft.startTime);
        setAddTaskDuration(draft.duration);
        setDragStartOffset(draft.startOffset);
        setDraftSelection(null);
        openCreateTaskModal(startDate);
    }, [openCreateTaskModal, setAddTaskDate, setAddTaskDuration, setAddTaskTime,
        setDraftSelection, setDragStartOffset, state.draftSelection]);
    const cancelDraftSelection = useCallback(() => {
        setDraftSelection(null);
        setAddTaskDate(null);
        setAddTaskTime(null);
        setAddTaskDuration(60);
        setDragStartOffset(0);
    }, [setAddTaskDate, setAddTaskDuration, setAddTaskTime, setDraftSelection, setDragStartOffset]);
    const prepareCreateTaskTime = useCallback((
        date: Date, hour: number, target: HTMLElement, clientY: number, duration = addTaskDuration
    ) => {
        const rect = target.getBoundingClientRect();
        const pixelsPerMinute = rect.height / 60;
        const minutes = Math.min(45, Math.max(0, Math.floor((clientY - rect.top) / pixelsPerMinute / 15) * 15));
        const draft = {
            date: new Date(date),
            startTime: `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
            startOffset: minutes * pixelsPerMinute,
            duration,
        };
        setAddTaskDate(draft.date);
        setTimeSlotHeight(rect.height);
        setDragStartOffset(draft.startOffset);
        setAddTaskTime(draft.startTime);
        return draft;
    }, [addTaskDuration, setAddTaskDate, setAddTaskTime, setDragStartOffset, setTimeSlotHeight]);
    const beginCreateTaskGesture = useCallback((date: Date, hour: number, target: HTMLElement, clientY: number) => {
        prepareCreateTaskTime(date, hour, target, clientY);
        setIsDragging(true);
        setDragStartHour(hour);
        setDragStartY(clientY);
        setHasDragged(false);
        setAddTaskDuration(30);
        setCurrentMouseY(clientY);
    }, [prepareCreateTaskTime, setAddTaskDuration, setCurrentMouseY, setDragStartHour,
        setDragStartY, setHasDragged, setIsDragging]);
    const cancelPendingTouchCreate = useCallback(() => {
        const listeners = pendingCreateListenersRef.current;
        if (listeners) {
            document.removeEventListener('pointermove', listeners.pointerMove);
            document.removeEventListener('pointerup', listeners.pointerUp);
            document.removeEventListener('pointercancel', listeners.pointerCancel);
            pendingCreateListenersRef.current = null;
        }
        pendingTouchCreateRef.current = null;
        setPendingTouchCreate(false);
    }, [pendingCreateListenersRef, pendingTouchCreateRef, setPendingTouchCreate]);
    const createPendingDraftSelection = useCallback(() => {
        const pending = pendingTouchCreateRef.current;
        if (!pending) return;
        const draft = prepareCreateTaskTime(pending.date, pending.hour, pending.target, pending.startY, 60);
        setAddTaskDuration(60);
        cancelPendingTouchCreate();
        setDraftSelection(draft);
    }, [cancelPendingTouchCreate, pendingTouchCreateRef, prepareCreateTaskTime,
        setAddTaskDuration, setDraftSelection]);
    const handleCreationPointerMove = useCallback((event: PointerEvent) => {
        if (!isDragging || dragStartHour === null || dragStartY === null) return;
        event.preventDefault();
        const deltaY = event.clientY - dragStartY;
        if (Math.abs(deltaY) > 5) setHasDragged(true);
        const pixelsPerMinute = state.timeSlotHeight / 60;
        const duration = Math.max(30, Math.round(deltaY / pixelsPerMinute / 15) * 15);
        setAddTaskDuration(duration);
        setCurrentMouseY(event.clientY);
    }, [dragStartHour, dragStartY, isDragging, setAddTaskDuration,
        setCurrentMouseY, setHasDragged, state.timeSlotHeight]);
    const handleCreationPointerUp = useCallback(() => {
        if (pendingTouchCreateRef.current) {
            createPendingDraftSelection();
        } else if (isDragging) {
            setIsDragging(false);
            setDragStartHour(null);
            setDragStartY(null);
            if (!hasDragged) {
                setAddTaskDuration(60);
                createDraftSelection(60);
            } else createDraftSelection();
            setHasDragged(false);
            setCurrentMouseY(null);
        }
    }, [createDraftSelection, createPendingDraftSelection, hasDragged, isDragging,
        pendingTouchCreateRef, setAddTaskDuration, setCurrentMouseY, setDragStartHour,
        setDragStartY, setHasDragged, setIsDragging]);
    const handleTimelineHourPointerDown = (date: Date, hour: number, event: React.PointerEvent) => {
        event.stopPropagation();
        cancelPendingTouchCreate();
        const pending = {
            date: new Date(date), hour, pointerId: event.pointerId, pointerType: event.pointerType,
            startY: event.clientY, target: event.currentTarget as HTMLElement,
        };
        pendingTouchCreateRef.current = pending;
        setPendingTouchCreate(true);
        const move = (next: PointerEvent) => {
            if (pending.pointerId !== next.pointerId) return;
            const movement = Math.abs(next.clientY - pending.startY);
            if (pending.pointerType === 'mouse' && movement > 5) {
                pending.target.setPointerCapture?.(pending.pointerId);
                cancelPendingTouchCreate();
                beginCreateTaskGesture(pending.date, pending.hour, pending.target, pending.startY);
                setHasDragged(true);
                setCurrentMouseY(next.clientY);
            } else if (pending.pointerType !== 'mouse' && movement > 8) cancelPendingTouchCreate();
        };
        const up = (next: PointerEvent) => { if (pending.pointerId === next.pointerId) createPendingDraftSelection(); };
        const cancel = (next: PointerEvent) => { if (pending.pointerId === next.pointerId) cancelPendingTouchCreate(); };
        pendingCreateListenersRef.current = { pointerMove: move, pointerUp: up, pointerCancel: cancel };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        document.addEventListener('pointercancel', cancel);
    };
    return {
        beginCreateTaskGesture, cancelDraftSelection, cancelPendingTouchCreate, confirmDraftSelection,
        handleCreationPointerMove, handleCreationPointerUp, handleTimelineHourPointerDown,
    };
};
